<template>
  <v-dialog
    :value="true"
    max-width="480"
    persistent
    @click:outside="$emit('close')"
  >
    <v-card>
      <v-card-title class="text-h6">
        {{ currentZoneId ? "Update Zone Assignment" : "Assign to Zone" }}
      </v-card-title>

      <v-card-text>
        <div v-if="currentZoneId && currentZoneName" class="mb-4">
          <span class="caption grey--text">Current zone: </span>
          <span class="font-weight-medium">{{ currentZoneName }}</span>
        </div>

        <v-text-field
          v-model="search"
          label="Search zones"
          dense
          outlined
          clearable
          prepend-inner-icon="mdi-magnify"
          class="mb-2"
        />

        <div v-if="zonesLoading" class="text-center py-4">
          <v-progress-circular indeterminate color="primary" size="28" />
        </div>

        <div
          v-else-if="filteredZones.length === 0"
          class="grey--text caption py-2"
        >
          No zones found.
        </div>

        <div v-else style="max-height: 280px; overflow-y: auto">
          <v-radio-group v-model="selectedZoneId" hide-details>
            <v-radio
              v-for="zone in filteredZones"
              :key="zone.id"
              :label="zone.name"
              :value="zone.id"
            />
          </v-radio-group>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('close')">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!canProceed"
          @click="onPrimaryAction"
        >
          {{ needsMoveConfirm ? "Next" : "Save Zone" }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Re-assigning an already-mapped unit goes through the shared
         move-confirmation step (same friction as the zone detail page). -->
    <DialogConfirmZoneMove
      :value="showMoveConfirm"
      :unit-name="unitName"
      :from-zone="currentZoneName"
      :to-zone="targetZoneName"
      :saving="saving"
      @confirm="confirmAndSave"
      @cancel="showMoveConfirm = false"
    />
  </v-dialog>
</template>

<script>
import useAlerts from "@tod-ui/composables/useAlerts";
import DialogConfirmZoneMove from "./DialogConfirmZoneMove.vue";
import { GET_FACILITY_ZONES, ASSIGN_UNIT_ZONE } from "../smart_units/graphql";

export default {
  name: "DialogUpdateZone",
  components: {
    DialogConfirmZoneMove,
  },
  props: {
    unitId: {
      type: String,
      required: true,
    },
    facilityId: {
      type: String,
      required: true,
    },
    currentZoneId: {
      type: String,
      default: null,
    },
    unitName: {
      type: String,
      default: "",
    },
  },
  setup() {
    const { addAlert } = useAlerts();
    return { addAlert };
  },
  data() {
    return {
      zones: [],
      zonesLoading: false,
      selectedZoneId: this.currentZoneId || null,
      search: "",
      saving: false,
      showMoveConfirm: false,
    };
  },
  computed: {
    currentZoneName() {
      const z = this.zones.find((z) => z.id === this.currentZoneId);
      return z ? z.name : null;
    },
    targetZoneName() {
      const z = this.zones.find((z) => z.id === this.selectedZoneId);
      return z ? z.name : null;
    },
    // A move-confirmation is needed only when reassigning an already-mapped
    // unit to a *different* zone. Unassigned units save directly.
    needsMoveConfirm() {
      return Boolean(
        this.currentZoneId &&
          this.selectedZoneId &&
          this.selectedZoneId !== this.currentZoneId
      );
    },
    canProceed() {
      if (!this.selectedZoneId) return false;
      // Nothing to do if the unit is already in the selected zone.
      return this.selectedZoneId !== this.currentZoneId;
    },
    filteredZones() {
      if (!this.search) return this.zones;
      const s = this.search.toLowerCase();
      return this.zones.filter((z) => z.name.toLowerCase().includes(s));
    },
  },
  mounted() {
    this.loadFacilityZones();
  },
  methods: {
    onPrimaryAction() {
      if (this.needsMoveConfirm) {
        this.showMoveConfirm = true;
      } else {
        this.saveZone();
      }
    },
    async confirmAndSave() {
      await this.saveZone();
      this.showMoveConfirm = false;
    },
    async loadFacilityZones() {
      this.zonesLoading = true;
      try {
        const { data } = await this.$apollo.query({
          query: GET_FACILITY_ZONES,
          variables: { facilityId: this.facilityId },
          fetchPolicy: "network-only",
        });
        this.zones = data?.organizationDescendants || [];
      } catch (e) {
        console.error("DialogUpdateZone: failed to load zones", e);
      } finally {
        this.zonesLoading = false;
      }
    },

    async saveZone() {
      if (!this.selectedZoneId) return;
      this.saving = true;
      try {
        await this.$apollo.mutate({
          mutation: ASSIGN_UNIT_ZONE,
          variables: {
            zoneId: this.selectedZoneId,
            unitIds: [this.unitId],
          },
        });
        this.addAlert({
          type: "success",
          message: "Zone assignment updated.",
          timeout: 10,
        });
        this.$emit("saved");
      } catch (e) {
        console.error("DialogUpdateZone: failed to save zone", e);
        this.addAlert({
          type: "error",
          message: "Failed to update zone assignment.",
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
